/**
 * ExpandableCallout — React (web) implementation.
 *
 * Mirrors the native component: a tappable callout header that discloses its
 * children inline. All styling references the constants in ExpandableCallout.ts.
 */

import React, { useId, useState, type ReactNode } from 'react';
import { Icon } from '../Icon/Icon.web';
import {
  EXPANDABLE_CALLOUT_RADIUS,
  EXPANDABLE_CALLOUT_PADDING,
  EXPANDABLE_CALLOUT_HEADER_GAP,
  EXPANDABLE_CALLOUT_PANEL_GAP,
  EXPANDABLE_CALLOUT_BACKGROUND,
  EXPANDABLE_CALLOUT_TEXT_COLOR,
  EXPANDABLE_CALLOUT_ICON_COLOR,
  EXPANDABLE_CALLOUT_ICON_SIZE,
  EXPANDABLE_CALLOUT_LEADING_ICON,
  EXPANDABLE_CALLOUT_CHEVRON_EXPANDED,
  EXPANDABLE_CALLOUT_CHEVRON_COLLAPSED,
  EXPANDABLE_CALLOUT_TITLE_TYPOGRAPHY,
  EXPANDABLE_CALLOUT_MIN_TOUCH_TARGET,
  EXPANDABLE_CALLOUT_REVEAL_DURATION,
  expandableCalloutAccessibleLabel,
  type ExpandableCalloutProps,
} from './ExpandableCallout';
import { ensureStyle, FOCUS_RING_OUTLINE } from '../../elements/focusStyle.web';
import { useReducedMotion } from '../../elements/useReduceMotion.web';

// The header button keeps the full touch-target height, but the card's own
// padding gives up the part the button already adds around a one-line title.
// So the collapsed card is padding + title + padding (54px), as the web info
// card draws it, while the button stays 48px tall. The panel puts the inset back
// so the open card's spacing is unchanged.
const WEB_HEADER_INSET =
  (EXPANDABLE_CALLOUT_MIN_TOUCH_TARGET - EXPANDABLE_CALLOUT_TITLE_TYPOGRAPHY.lineHeight) / 2;

const CALLOUT_FOCUS_STYLE_ID = 'galapagos-expandable-callout-focus-visible';
const CALLOUT_ROOT_SELECTOR = '[data-galapagos-expandable-callout="true"]';

// Keyboard focus lands on the header button, but the ring wraps the whole card
// (it follows the card's corner radius). The header's own outline is removed
// only where `:has()` can draw the card ring, so focus always stays visible.
function ensureExpandableCalloutFocusStyle(): void {
  ensureStyle(
    CALLOUT_FOCUS_STYLE_ID,
    `@supports selector(:has(*)) {` +
      ` ${CALLOUT_ROOT_SELECTOR} > button:focus { outline: none; }` +
      ` ${CALLOUT_ROOT_SELECTOR}:has(> button:focus-visible) { ${FOCUS_RING_OUTLINE} }` +
      ` }`,
  );
}

export interface WebExpandableCalloutProps extends Partial<ExpandableCalloutProps> {
  title: string;
  children?: ReactNode;
  actionId?: string;
  'data-testid'?: string;
}

export function ExpandableCallout({
  title,
  defaultExpanded = false,
  children,
  accessibilityLabel,
  actionId = 'ui.expandable-callout.toggle',
  'data-testid': dataTestId,
}: WebExpandableCalloutProps) {
  ensureExpandableCalloutFocusStyle();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const panelId = useId();
  const reduceMotion = useReducedMotion();

  const containerStyle: React.CSSProperties = {
    backgroundColor: EXPANDABLE_CALLOUT_BACKGROUND,
    borderRadius: EXPANDABLE_CALLOUT_RADIUS,
    paddingTop: EXPANDABLE_CALLOUT_PADDING - WEB_HEADER_INSET,
    paddingBottom: EXPANDABLE_CALLOUT_PADDING - WEB_HEADER_INSET,
    paddingLeft: EXPANDABLE_CALLOUT_PADDING,
    paddingRight: EXPANDABLE_CALLOUT_PADDING,
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: EXPANDABLE_CALLOUT_HEADER_GAP,
    width: '100%',
    background: 'none',
    border: 'none',
    paddingTop: WEB_HEADER_INSET,
    paddingBottom: WEB_HEADER_INSET,
    paddingLeft: 0,
    paddingRight: 0,
    margin: 0,
    minHeight: EXPANDABLE_CALLOUT_MIN_TOUCH_TARGET,
    cursor: 'pointer',
    textAlign: 'left',
  };

  const titleStyle: React.CSSProperties = {
    flex: 1,
    color: EXPANDABLE_CALLOUT_TEXT_COLOR,
    fontFamily: EXPANDABLE_CALLOUT_TITLE_TYPOGRAPHY.fontFamily,
    fontWeight: EXPANDABLE_CALLOUT_TITLE_TYPOGRAPHY.fontWeight,
    fontSize: EXPANDABLE_CALLOUT_TITLE_TYPOGRAPHY.fontSize,
    lineHeight: `${EXPANDABLE_CALLOUT_TITLE_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: EXPANDABLE_CALLOUT_TITLE_TYPOGRAPHY.letterSpacing,
  };

  return (
    <div style={containerStyle} data-testid={dataTestId} data-galapagos-expandable-callout="true">
      <button
        type="button"
        data-analytics-action={actionId}
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-controls={panelId}
        aria-label={expandableCalloutAccessibleLabel(title, accessibilityLabel)}
        style={headerStyle}
        data-testid={dataTestId ? `${dataTestId}-header` : undefined}
      >
        <Icon
          name={EXPANDABLE_CALLOUT_LEADING_ICON}
          size={EXPANDABLE_CALLOUT_ICON_SIZE}
          color={EXPANDABLE_CALLOUT_ICON_COLOR}
          aria-hidden
        />
        <span style={titleStyle}>{title}</span>
        <Icon
          name={
            expanded ? EXPANDABLE_CALLOUT_CHEVRON_EXPANDED : EXPANDABLE_CALLOUT_CHEVRON_COLLAPSED
          }
          size={EXPANDABLE_CALLOUT_ICON_SIZE}
          color={EXPANDABLE_CALLOUT_ICON_COLOR}
          aria-hidden
        />
      </button>
      <div
        id={panelId}
        aria-hidden={!expanded}
        // Collapsed: `inert` takes the clipped subtree out of the tab order and
        // the a11y tree, so a focusable child can't be reached while hidden.
        inert={expanded ? undefined : true}
        data-testid={dataTestId ? `${dataTestId}-panel` : undefined}
        style={{
          display: 'grid',
          // 0fr → 1fr animates the disclosed height without a hard-coded value;
          // the inner wrapper's `min-height: 0` + `overflow: hidden` lets the
          // row track collapse fully.
          gridTemplateRows: expanded ? '1fr' : '0fr',
          opacity: expanded ? 1 : 0,
          transition: reduceMotion
            ? undefined
            : `grid-template-rows ${EXPANDABLE_CALLOUT_REVEAL_DURATION}ms ease, opacity ${EXPANDABLE_CALLOUT_REVEAL_DURATION}ms ease`,
        }}
      >
        <div style={{ minHeight: 0, overflow: 'hidden' }}>
          {/* Padding lives inside the animated region so the header→panel gap
              collapses to nothing when closed. The header button already adds
              the inset above, and the card gave it up below. */}
          <div
            style={{
              paddingTop: EXPANDABLE_CALLOUT_PANEL_GAP - WEB_HEADER_INSET,
              paddingBottom: WEB_HEADER_INSET,
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
